import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Card,
  CardContent,
  makeStyles,
  Grid,
  TextField,
  Button,
  MenuItem,
} from "@material-ui/core";
import axios from "axios";
import CSVReader from "react-csv-reader";
import { CSVDownload } from "react-csv";
import Navigation from "./Navigation";

const ROLE_OPTIONS = ["Session", "Master", "Exhibitor"];

const ROLE_COLOR = (role) => {
  if (role === "Master") return "#f59e0b";
  if (role === "Session") return "#5e94c8";
  return "#e81b23";
};

const ManageExhibitors = () => {
  const classes = useStyles();

  // ── Filter: "add" | "list" ──
  const [view, setView] = useState("add");

  // ── Add-single-exhibitor form fields ──
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("Session");
  const [scores, setScores] = useState("");

  // ── Locally-added exhibitors (mirrors GenerateQr's qrResultsGrid) ──
  const [added, setAdded] = useState([]);

  // ── CSV bulk upload ──
  const [isGenerating, setIsGenerating] = useState(false);
  const [progressText, setProgressText] = useState("");
  const [downloadFlag, setDownloadFlag] = useState(false);
  const [sampleData] = useState([
    {
      name: "Sample Exhibitor",
      email: "sample@example.com",
      password: "123456",
      role: "Session",
      scores: 0,
    },
  ]);

  // ── All-list view ──
  const [allExhibitors, setAllExhibitors] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchAllExhibitors = useCallback(async () => {
    try {
      setListLoading(true);
      const resp = await axios.get(
        process.env.REACT_APP_ADMIN + "/exhibitor/getAll",
      );
      setAllExhibitors(resp.data || []);
    } catch (error) {
      console.log(error);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === "list") {
      fetchAllExhibitors();
    }
  }, [view, fetchAllExhibitors]);

  const resetForm = () => {
    setName("");
    setEmail("");
    setPassword("");
    setRole("Session");
    setScores("");
  };

  const handleAddExhibitor = async () => {
    if (!email) {
      alert("Email is required");
      return;
    }
    try {
      const resp = await axios.post(
        process.env.REACT_APP_ADMIN + "/exhibitor/addOne",
        { name, email, password, role, scores },
      );
      setAdded((current) => [resp.data, ...current]);
      resetForm();
    } catch (error) {
      alert(
        error?.response?.data?.message ||
          "Something went wrong while adding the exhibitor.",
      );
      console.log(error);
    }
  };

  const handleSampleCsv = () => {
    setDownloadFlag(true);
    setTimeout(() => setDownloadFlag(false), 5000);
  };

  const handleCsvUpload = async (csvData) => {
    try {
      if (!csvData.length) {
        alert("Invalid File data");
        return;
      }
      setIsGenerating(true);
      setProgressText("Uploading exhibitors…");

      const resp = await axios.post(
        process.env.REACT_APP_ADMIN + "/exhibitor/addMultiple",
        { csvData },
      );

      setProgressText("Done!");
      alert(`${resp.data?.length || 0} exhibitors saved successfully!`);
      setAdded((current) => [...(resp.data || []), ...current]);
    } catch (error) {
      alert("Error uploading CSV. Please check the format and try again.");
      console.log(error);
    } finally {
      setIsGenerating(false);
      setProgressText("");
    }
  };

  const filteredList = allExhibitors.filter((ex) => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return (
      ex?.name?.toLowerCase().includes(t) ||
      ex?.email?.toLowerCase().includes(t) ||
      ex?.role?.toLowerCase().includes(t)
    );
  });

  return (
    <Container className={classes.conatiner}>
      <div className={classes.bgOrb1}></div>
      <div className={classes.bgOrb2}></div>
      <div className={classes.bgOrb3}></div>

      <Card className={classes.card}>
        <CardContent>
          <Navigation />

          <div className={classes.contentWrapper}>
            {/* ── Filter bar ── */}
            <div className={classes.filterBar}>
              <button
                className={classes.filterBtn}
                style={
                  view === "add"
                    ? {
                        background: "rgba(232,27,35,0.15)",
                        border: "1px solid rgba(232,27,35,0.55)",
                        color: "#e81b23",
                      }
                    : {}
                }
                onClick={() => setView("add")}
              >
                Add Exhibitor
              </button>
              <button
                className={classes.filterBtn}
                style={
                  view === "list"
                    ? {
                        background: "rgba(232,27,35,0.15)",
                        border: "1px solid rgba(232,27,35,0.55)",
                        color: "#e81b23",
                      }
                    : {}
                }
                onClick={() => setView("list")}
              >
                All List
              </button>
            </div>

            {view === "add" ? (
              <Grid container spacing={4}>
                {/* ── Single exhibitor form ── */}
                <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                  <div className={classes.formSection}>
                    <h3 className={classes.sectionTitle}>
                      Add Single Exhibitor
                    </h3>

                    <TextField
                      className={classes.field}
                      label="Name"
                      variant="outlined"
                      fullWidth
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                    <TextField
                      className={classes.field}
                      label="Email"
                      variant="outlined"
                      fullWidth
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <TextField
                      className={classes.field}
                      label="Password"
                      variant="outlined"
                      fullWidth
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <TextField
                      className={classes.field}
                      label="Role"
                      variant="outlined"
                      select
                      fullWidth
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <MenuItem key={r} value={r}>
                          {r}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      className={classes.field}
                      label="Scores"
                      variant="outlined"
                      type="number"
                      fullWidth
                      value={scores}
                      onChange={(e) => setScores(e.target.value)}
                    />

                    <Button
                      className={classes.btn}
                      variant="contained"
                      onClick={handleAddExhibitor}
                    >
                      Add Exhibitor
                    </Button>

                    {added.length > 0 && (
                      <div className={classes.addedGrid}>
                        {added.map((ex, index) => (
                          <div key={ex?._id || index} className={classes.addedCard}>
                            <span
                              className={classes.addedRoleDot}
                              style={{ background: ROLE_COLOR(ex?.role) }}
                            />
                            <div className={classes.addedInfo}>
                              <p className={classes.addedName}>
                                {ex?.name || "—"}
                              </p>
                              <p className={classes.addedEmail}>
                                {ex?.email}
                              </p>
                            </div>
                            <span
                              className={classes.addedRoleBadge}
                              style={{
                                color: ROLE_COLOR(ex?.role),
                                borderColor: ROLE_COLOR(ex?.role),
                              }}
                            >
                              {ex?.role || "—"}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Grid>

                {/* ── CSV bulk upload ── */}
                <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                  <div className={classes.uploadSection}>
                    <h3 className={classes.sectionTitle}>
                      Add Multiple Exhibitors
                    </h3>
                    <p className={classes.sectionDescription}>
                      Upload a CSV file to add exhibitors in bulk. Required
                      columns:{" "}
                      <strong style={{ color: "#e81b23" }}>
                        name, email, password, role, scores
                      </strong>
                    </p>

                    <div className={classes.csvReaderWrapper}>
                      {isGenerating ? (
                        <div className={classes.progressWrap}>
                          <div className={classes.spinner}></div>
                          <p className={classes.progressText}>
                            {progressText}
                          </p>
                        </div>
                      ) : (
                        <>
                          <p className={classes.uploadHint}>
                            Drop your CSV file here or click to browse
                          </p>
                          <CSVReader
                            parserOptions={{ header: true }}
                            onFileLoaded={(data) => handleCsvUpload(data)}
                          />
                        </>
                      )}
                    </div>

                    <div className={classes.divider}>
                      <span className={classes.dividerLine}></span>
                      <span className={classes.dividerText}>or</span>
                      <span className={classes.dividerLine}></span>
                    </div>

                    <Button
                      className={classes.btnOutline}
                      variant="outlined"
                      onClick={handleSampleCsv}
                    >
                      Download Sample CSV
                    </Button>
                    {downloadFlag ? (
                      <CSVDownload data={sampleData} target="_blank" />
                    ) : null}
                  </div>
                </Grid>
              </Grid>
            ) : (
              /* ── All List view ── */
              <div className={classes.listSection}>
                <div className={classes.listHeader}>
                  <h3 className={classes.sectionTitle}>
                    All Exhibitors ({allExhibitors.length})
                  </h3>
                  <TextField
                    className={classes.searchField}
                    label="Search by name / email / role"
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                {listLoading ? (
                  <div className={classes.progressWrap}>
                    <div className={classes.spinner}></div>
                    <p className={classes.progressText}>Loading exhibitors…</p>
                  </div>
                ) : filteredList.length === 0 ? (
                  <p className={classes.sectionDescription}>
                    No exhibitors found.
                  </p>
                ) : (
                  <div className={classes.listGrid}>
                    {filteredList.map((ex) => (
                      <div key={ex._id} className={classes.listCard}>
                        <div className={classes.listCardHeader}>
                          <span
                            className={classes.listRoleBadge}
                            style={{
                              color: ROLE_COLOR(ex?.role),
                              borderColor: ROLE_COLOR(ex?.role),
                              background: `${ROLE_COLOR(ex?.role)}1a`,
                            }}
                          >
                            {ex?.role || "—"}
                          </span>
                          <span className={classes.listScore}>
                            {ex?.scores ?? 0} pts
                          </span>
                        </div>
                        <p className={classes.listName}>{ex?.name || "—"}</p>
                        <p className={classes.listEmail}>{ex?.email}</p>
                        <p className={classes.listUsers}>
                          Checked-in users:{" "}
                          <strong>{ex?.users?.length ?? 0}</strong>
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </Container>
  );
};

const useStyles = makeStyles((theme) => ({
  "@keyframes fadeIn": {
    from: { opacity: 0, transform: "translateY(28px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes orbFloat1": {
    "0%, 100%": { transform: "translate(0, 0) scale(1)" },
    "33%": { transform: "translate(60px, -80px) scale(1.15)" },
    "66%": { transform: "translate(-40px, 40px) scale(0.9)" },
  },
  "@keyframes orbFloat2": {
    "0%, 100%": { transform: "translate(0, 0) scale(1)" },
    "33%": { transform: "translate(-70px, 50px) scale(1.1)" },
    "66%": { transform: "translate(50px, -60px) scale(0.95)" },
  },
  "@keyframes orbFloat3": {
    "0%, 100%": { transform: "translate(0, 0) scale(1)" },
    "50%": { transform: "translate(40px, 60px) scale(1.2)" },
  },
  "@keyframes spin": {
    from: { transform: "rotate(0deg)" },
    to: { transform: "rotate(360deg)" },
  },
  "@keyframes cardPopIn": {
    from: { opacity: 0, transform: "scale(0.9) translateY(12px)" },
    to: { opacity: 1, transform: "scale(1) translateY(0)" },
  },

  conatiner: {
    background:
      "linear-gradient(160deg, #0a0a0f 0%, #12121a 40%, #0d0d15 100%)",
    height: "100vh",
    width: "100vw",
    maxWidth: "100vw",
    position: "relative",
    padding: "0 !important",
    overflow: "hidden",
  },
  bgOrb1: {
    position: "fixed",
    top: "-15%",
    left: "-10%",
    width: 600,
    height: 600,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(232,27,35,0.18) 0%, rgba(232,27,35,0.04) 50%, transparent 70%)",
    animation: "$orbFloat1 12s ease-in-out infinite",
    pointerEvents: "none",
    zIndex: 0,
    filter: "blur(8px)",
  },
  bgOrb2: {
    position: "fixed",
    bottom: "-20%",
    right: "-10%",
    width: 700,
    height: 700,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(180,10,18,0.14) 0%, rgba(120,0,10,0.05) 50%, transparent 70%)",
    animation: "$orbFloat2 16s ease-in-out infinite",
    pointerEvents: "none",
    zIndex: 0,
    filter: "blur(12px)",
  },
  bgOrb3: {
    position: "fixed",
    top: "40%",
    left: "30%",
    width: 400,
    height: 400,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(255,80,80,0.06) 0%, transparent 70%)",
    animation: "$orbFloat3 20s ease-in-out infinite",
    pointerEvents: "none",
    zIndex: 0,
    filter: "blur(20px)",
  },

  card: {
    background: "rgba(14,14,20,0.88)",
    backdropFilter: "blur(30px)",
    WebkitBackdropFilter: "blur(30px)",
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow:
      "0 8px 64px 0 rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.05)",
    height: "99.6%",
    margin: "0 !important",
    padding: "0 !important",
    overflowY: "scroll",
    position: "relative",
    zIndex: 1,
    animation: "$fadeIn 0.7s cubic-bezier(0.22,1,0.36,1)",
    "&::-webkit-scrollbar": { width: "6px" },
    "&::-webkit-scrollbar-track": {
      background: "rgba(255,255,255,0.03)",
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "linear-gradient(180deg,#e81b23 0%,#7b0008 100%)",
      borderRadius: "10px",
    },
  },

  contentWrapper: {
    padding: theme.spacing(4),
    animation: "$fadeIn 0.6s cubic-bezier(0.22,1,0.36,1)",
    [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
  },

  // ── Filter bar ──
  filterBar: {
    display: "flex",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
  },
  filterBtn: {
    padding: "10px 24px",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.1)",
    background: "rgba(255,255,255,0.03)",
    color: "rgba(200,200,215,0.7)",
    fontWeight: 700,
    fontSize: "0.9rem",
    letterSpacing: "0.02em",
    cursor: "pointer",
    transition: "all 0.25s cubic-bezier(0.22,1,0.36,1)",
    outline: "none",
    "&:hover": {
      background: "rgba(255,255,255,0.07)",
      color: "#fff",
    },
  },

  formSection: {
    background: "linear-gradient(160deg,#111118 0%,#0c0c14 100%)",
    padding: theme.spacing(4),
    borderRadius: "20px",
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow:
      "0 4px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)",
    transition: "box-shadow 0.4s ease, border-color 0.4s ease",
    animation: "$fadeIn 0.6s cubic-bezier(0.22,1,0.36,1)",
    "&:hover": {
      borderColor: "rgba(232,27,35,0.3)",
      boxShadow:
        "0 8px 40px rgba(232,27,35,0.15), inset 0 1px 0 rgba(255,255,255,0.07)",
    },
  },
  uploadSection: {
    background: "linear-gradient(160deg,#111118 0%,#0c0c14 100%)",
    padding: theme.spacing(4),
    borderRadius: "20px",
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow:
      "0 4px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)",
    transition: "box-shadow 0.4s ease, border-color 0.4s ease",
    animation: "$fadeIn 0.6s cubic-bezier(0.22,1,0.36,1) 0.1s backwards",
    "&:hover": {
      borderColor: "rgba(232,27,35,0.3)",
      boxShadow:
        "0 8px 40px rgba(232,27,35,0.15), inset 0 1px 0 rgba(255,255,255,0.07)",
    },
  },

  sectionTitle: {
    color: "#f0f0f5",
    fontSize: "1.4rem",
    fontWeight: 700,
    marginTop: 0,
    marginBottom: theme.spacing(3),
    letterSpacing: "0.03em",
    textShadow: "0 0 20px rgba(232,27,35,0.4)",
  },
  sectionDescription: {
    color: "rgba(180,180,200,0.55)",
    fontSize: "0.9rem",
    marginTop: 0,
    marginBottom: theme.spacing(3),
    lineHeight: 1.7,
  },

  field: {
    marginBottom: theme.spacing(2),
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.07)",
      borderRadius: "10px",
      color: "#f0f0f5",
      transition: "all 0.3s ease",
      "& input": { color: "#f0f0f5" },
      "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
      "&:hover": {
        backgroundColor: "rgba(255,255,255,0.10)",
        "& fieldset": { borderColor: "rgba(232,27,35,0.5)" },
      },
      "&.Mui-focused": {
        backgroundColor: "rgba(232,27,35,0.06)",
        boxShadow: "0 0 0 3px rgba(232,27,35,0.12)",
        "& fieldset": { borderColor: "#e81b23", borderWidth: "2px" },
      },
    },
    "& .MuiInputLabel-root": { color: "rgba(180,180,200,0.6)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#e81b23", fontWeight: 600 },
  },

  btn: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    backgroundColor: "#e81b23",
    color: "#fff",
    fontWeight: 700,
    padding: "12px 32px",
    borderRadius: "10px",
    textTransform: "none",
    fontSize: "0.95rem",
    letterSpacing: "0.02em",
    boxShadow: "0 4px 16px rgba(232,27,35,0.35)",
    transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
    "&:hover": {
      backgroundColor: "#c8121a",
      transform: "translateY(-3px) scale(1.02)",
      boxShadow: "0 8px 28px rgba(232,27,35,0.55)",
    },
  },
  btnOutline: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    backgroundColor: "transparent",
    color: "#e81b23",
    fontWeight: 700,
    padding: "11px 28px",
    borderRadius: "10px",
    border: "1.5px solid rgba(232,27,35,0.5)",
    textTransform: "none",
    fontSize: "0.95rem",
    letterSpacing: "0.02em",
    display: "flex",
    alignItems: "center",
    transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
    "&:hover": {
      backgroundColor: "rgba(232,27,35,0.1)",
      borderColor: "#e81b23",
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(232,27,35,0.25)",
    },
  },

  csvReaderWrapper: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(4, 3),
    background: "rgba(232,27,35,0.04)",
    borderRadius: "14px",
    border: "2px dashed rgba(232,27,35,0.3)",
    textAlign: "center",
    minHeight: "160px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
    "&:hover": {
      borderColor: "#e81b23",
      background: "rgba(232,27,35,0.08)",
      boxShadow: "0 0 0 4px rgba(232,27,35,0.06)",
    },
  },
  uploadHint: {
    color: "rgba(180,180,200,0.5)",
    fontSize: "0.82rem",
    margin: "0 0 12px 0",
    letterSpacing: "0.02em",
  },

  progressWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "14px",
    padding: theme.spacing(2, 0),
  },
  spinner: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    border: "3px solid rgba(232,27,35,0.2)",
    borderTopColor: "#e81b23",
    animation: "$spin 0.8s linear infinite",
  },
  progressText: {
    color: "rgba(200,200,215,0.7)",
    fontSize: "0.85rem",
    margin: 0,
    letterSpacing: "0.02em",
  },

  divider: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    margin: theme.spacing(3, 0),
  },
  dividerLine: {
    flex: 1,
    height: "1px",
    background: "rgba(255,255,255,0.08)",
    borderRadius: "1px",
  },
  dividerText: {
    color: "rgba(180,180,200,0.35)",
    fontSize: "0.78rem",
    fontWeight: 500,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },

  // ── Locally-added exhibitors after single-add ──
  addedGrid: {
    marginTop: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  addedCard: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "12px",
    padding: theme.spacing(1.5, 2),
    animation: "$cardPopIn 0.35s cubic-bezier(0.22,1,0.36,1) backwards",
  },
  addedRoleDot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
  addedInfo: { flex: 1, minWidth: 0 },
  addedName: {
    color: "#f0f0f5",
    fontWeight: 700,
    fontSize: "0.9rem",
    margin: 0,
  },
  addedEmail: {
    color: "rgba(180,180,200,0.55)",
    fontSize: "0.78rem",
    margin: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  addedRoleBadge: {
    fontSize: "0.7rem",
    fontWeight: 700,
    padding: "3px 10px",
    borderRadius: "20px",
    border: "1px solid",
    letterSpacing: "0.04em",
    flexShrink: 0,
  },

  // ── All List view ──
  listSection: {
    background: "linear-gradient(160deg,#111118 0%,#0c0c14 100%)",
    padding: theme.spacing(4),
    borderRadius: "20px",
    border: "1px solid rgba(255,255,255,0.08)",
    animation: "$fadeIn 0.6s cubic-bezier(0.22,1,0.36,1)",
  },
  listHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  searchField: {
    minWidth: 260,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.07)",
      borderRadius: "10px",
      color: "#f0f0f5",
      "& input": { color: "#f0f0f5" },
      "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
    },
    "& .MuiInputLabel-root": { color: "rgba(180,180,200,0.6)" },
  },

  listGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  listCard: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "14px",
    padding: theme.spacing(2.5),
    transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
    animation: "$cardPopIn 0.4s cubic-bezier(0.22,1,0.36,1) backwards",
    "&:hover": {
      borderColor: "rgba(232,27,35,0.35)",
      transform: "translateY(-4px)",
      boxShadow: "0 10px 32px rgba(232,27,35,0.15)",
    },
  },
  listCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  listRoleBadge: {
    fontSize: "0.68rem",
    fontWeight: 700,
    padding: "3px 10px",
    borderRadius: "20px",
    border: "1px solid",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  listScore: {
    color: "rgba(200,200,215,0.6)",
    fontSize: "0.75rem",
    fontWeight: 600,
  },
  listName: {
    color: "#f0f0f5",
    fontWeight: 700,
    fontSize: "1rem",
    margin: "4px 0 2px",
  },
  listEmail: {
    color: "rgba(180,180,200,0.55)",
    fontSize: "0.8rem",
    margin: "0 0 8px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  listUsers: {
    color: "rgba(180,180,200,0.6)",
    fontSize: "0.78rem",
    margin: 0,
  },
}));

export default ManageExhibitors;